import { Navigate, type RouteObject } from 'react-router';
// The /dom entry wires ReactDOM.flushSync into <RouterProvider>: without it,
// navigate(..., { flushSync: true }) falls back to a startTransition (with a
// warnOnce in dev — see goods-receiving wizard-back regression test). Global
// navigation behavior is unchanged — flushSync only applies where a
// navigation opts in.
import { RouterProvider } from 'react-router/dom';
import ProtectedRoute from '../components/protected-route';
import { RootLayout } from '../components/root-layout';
import { RouteErrorScreen } from '../components/route-error-screen';
import { RoutesList } from '../config/routes-list';
import { sentryCreateBrowserRouter } from '../config/sentry';
import { AdminInviteAcceptPage } from '../pages/admin-invite/admin-invite-accept-page';
import { SignInPage } from '../pages/auth/sign-in-page';
import { SignUpPage } from '../pages/auth/sign-up-page';
import { AcceptInvitePage } from '../pages/invite/accept-invite-page';
import Logout from '../pages/Logout';
import { SignupLinkPage } from '../pages/signup-link/signup-link-page';
import { lazyRoute, lazyRouteNamed } from './lazy-route';
import { protectedRoutes } from './protected-routes';

const GuideRoute: RouteObject = {
  path: RoutesList.GUIDE,
  ...lazyRouteNamed(() => import('../pages/guide/guide-page'), 'GuidePage'),
};

const GuideTopicRoute: RouteObject = {
  path: RoutesList.GUIDE_TOPIC,
  ...lazyRouteNamed(() => import('../pages/guide/guide-page'), 'GuidePage'),
};

const router = sentryCreateBrowserRouter([
  {
    element: <RootLayout />,
    errorElement: <RouteErrorScreen />,
    children: [
      {
        element: <ProtectedRoute />,
        children: [...protectedRoutes, GuideRoute, GuideTopicRoute],
      },
      {
        path: RoutesList.LOGIN,
        element: <SignInPage />,
      },
      {
        path: RoutesList.REGISTER,
        element: <SignUpPage />,
      },
      {
        path: RoutesList.LOGOUT,
        element: <Logout />,
      },
      {
        path: RoutesList.SELECT_COMPANY,
        ...lazyRouteNamed(
          () => import('../pages/select-company/select-company-page'),
          'SelectCompanyPage',
        ),
      },
      {
        path: RoutesList.ONBOARDING,
        ...lazyRouteNamed(() => import('../pages/onboarding/onboarding-page'), 'OnboardingPage'),
      },
      {
        path: RoutesList.TRIAL_EXPIRED,
        ...lazyRouteNamed(
          () => import('../pages/subscription/trial-expired-page'),
          'TrialExpiredPage',
        ),
      },
      {
        path: RoutesList.SET_PASSWORD,
        ...lazyRouteNamed(
          () => import('../pages/set-password/set-password-page'),
          'SetPasswordPage',
        ),
      },
      {
        path: RoutesList.ACCEPT_TERMS,
        ...lazyRouteNamed(
          () => import('../pages/accept-terms/accept-terms-page'),
          'AcceptTermsPage',
        ),
      },
      {
        path: '/invite/:token',
        element: <AcceptInvitePage />,
      },
      {
        path: '/admin-invite/:token',
        element: <AdminInviteAcceptPage />,
      },
      {
        path: '/signup-link/:token',
        element: <SignupLinkPage />,
      },
      {
        // Bare path for the OAuth sso-callback re-entry (token stripped from URL
        // before the social redirect). Without it the return lands on the
        // catch-all → HOME and the Google/social signup transfer is lost.
        path: '/signup-link',
        element: <SignupLinkPage />,
      },
      {
        path: '/public/lots/:id',
        ...lazyRouteNamed(() => import('../pages/lots/public-lot-page'), 'PublicLotPage'),
      },
      {
        path: RoutesList.PUBLIC_MENU,
        ...lazyRoute(() => import('../pages/public-menu/public-menu-page')),
      },
      {
        path: RoutesList.PUBLIC_DDT,
        ...lazyRouteNamed(
          () => import('../pages/transport-documents/public-ddt-page'),
          'PublicDdtPage',
        ),
      },
      {
        path: '/public/haccp-archive/:token',
        ...lazyRouteNamed(
          () => import('../pages/public/haccp-archive-download-page'),
          'HaccpArchiveDownloadPage',
        ),
      },
      {
        path: RoutesList.INSPECTION_PORTAL,
        ...lazyRouteNamed(
          () => import('../pages/inspection-portal/inspection-portal-page'),
          'InspectionPortalPage',
        ),
      },
      {
        path: RoutesList.SURVEY_INVITE_PORTAL,
        ...lazyRouteNamed(
          () => import('../pages/public-survey-invite/public-survey-invite-page'),
          'PublicSurveyInvitePage',
        ),
      },
      {
        path: '/public/counter-register/:slug/:code?',
        ...lazyRouteNamed(
          () => import('../pages/public-counter-register/public-counter-register-page'),
          'PublicCounterRegisterPage',
        ),
      },
      {
        path: RoutesList.WHATSAPP_MAGIC_LINK,
        ...lazyRouteNamed(
          () => import('../pages/whatsapp-link/whatsapp-magic-link-page'),
          'WhatsAppMagicLinkPage',
        ),
      },
      {
        path: '/public/billing/:token',
        ...lazyRouteNamed(
          () => import('../pages/public/billing-payment-link-page'),
          'BillingPaymentLinkPage',
        ),
      },
      {
        path: '/public/unsubscribe',
        ...lazyRouteNamed(
          () => import('../pages/public/email-unsubscribe-page'),
          'EmailUnsubscribePage',
        ),
      },
      {
        path: RoutesList.PRIVACY,
        ...lazyRouteNamed(() => import('../pages/privacy/privacy-page'), 'PrivacyPage'),
      },
      {
        path: RoutesList.TERMS_OF_SERVICE,
        ...lazyRouteNamed(
          () => import('../pages/terms-of-service/terms-of-service-page'),
          'TermsOfServicePage',
        ),
      },
      {
        path: RoutesList.DPA,
        ...lazyRouteNamed(() => import('../pages/dpa/dpa-page'), 'DpaPage'),
      },
      {
        path: RoutesList.SUB_PROCESSORS,
        ...lazyRouteNamed(
          () => import('../pages/sub-processors/sub-processors-page'),
          'SubProcessorsPage',
        ),
      },
      {
        path: '*',
        element: <Navigate to={RoutesList.HOME} replace />,
      },
    ],
  },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}

export default router;
